import {
  CustomField,
  WorkflowSettings,
  WorkflowTemplate,
} from '../../types';
import { NON_INPUT_KEYS, validateFormFields } from './validateForm';

export type ProblemSeverity = 'error' | 'warning';
export type ProblemArea = 'Form fields' | 'Workflow' | 'Custom fields';

export interface FormProblem {
  id: string;
  severity: ProblemSeverity;
  area: ProblemArea;
  message: string;
  detail?: string;
}

export interface FormProblemsInput {
  fields: any[];
  workflowSettings?: WorkflowSettings | null;
  customFields: CustomField[];
  submitEveryStep: boolean;
  /** null until the template list has loaded. */
  templates: WorkflowTemplate[] | null;
  templatesFailed: boolean;
}

// Mirrors the backend's custom field key rules (services/workflow/customFields.ts).
const CUSTOM_KEY = /^[A-Za-z_][A-Za-z0-9_.-]*$/;

/**
 * Whether a custom field can supply a value: it needs a usable key and either
 * a default or the URL query opt-in (a blank default with no query has nothing).
 */
export const customFieldProvides = (row: CustomField): boolean =>
  CUSTOM_KEY.test(row.key.trim()) && (row.value.trim() !== '' || Boolean(row.fromQuery));

/** Names of the form's input fields, i.e. those that produce an answer. */
export const inputFieldNames = (fields: any[]): string[] =>
  (Array.isArray(fields) ? fields : [])
    .filter((f) => f?.field_name && !f?.static && !NON_INPUT_KEYS.has(f?.key))
    .map((f) => String(f.field_name));

const list = (names: string[]) => names.map((n) => `"${n}"`).join(', ');

/**
 * Everything wrong (errors) or questionable (warnings) with the form as it is
 * being built. Errors are things that will break or silently lose data;
 * warnings are things that work but are probably not intended.
 */
export const collectFormProblems = ({
  fields,
  workflowSettings,
  customFields,
  submitEveryStep,
  templates,
  templatesFailed,
}: FormProblemsInput): FormProblem[] => {
  const problems: FormProblem[] = [];
  const add = (p: Omit<FormProblem, 'id'>) =>
    problems.push({ ...p, id: `${p.area}:${problems.length}` });

  // ── Form fields ──────────────────────────────────────────────────────────
  validateFormFields(fields).forEach((issue) => {
    add({
      severity: 'error',
      area: 'Form fields',
      message:
        issue.index < 0
          ? issue.message
          : `Field ${issue.index + 1}: ${issue.blockName}${issue.label ? ` "${issue.label}"` : ''} ${issue.message}`,
    });
  });

  const formNames = new Set(inputFieldNames(fields));

  // ── Custom fields ────────────────────────────────────────────────────────
  const seen = new Set<string>();
  const reportedDuplicates = new Set<string>();
  const providedByCustom = new Set<string>();
  const workflowOn = Boolean(workflowSettings?.enabled);

  (customFields || []).forEach((row) => {
    const key = row.key.trim();
    if (!key && !row.value) return; // an untouched blank row is ignored
    if (!key) {
      add({
        severity: 'error',
        area: 'Custom fields',
        message: 'A custom field has a value but no key.',
        detail: 'It will not be added to submissions.',
      });
      return;
    }
    if (!CUSTOM_KEY.test(key)) {
      add({
        severity: 'error',
        area: 'Custom fields',
        message: `Custom field key "${key}" is not valid.`,
        detail: 'Start with a letter or underscore; use only letters, numbers, _ . and -.',
      });
      return;
    }
    if (seen.has(key)) {
      if (!reportedDuplicates.has(key)) {
        reportedDuplicates.add(key);
        add({
          severity: 'error',
          area: 'Custom fields',
          message: `Custom field key "${key}" is used more than once.`,
          detail: 'Only the first one is used.',
        });
      }
      return;
    }
    seen.add(key);
    if (customFieldProvides(row)) providedByCustom.add(key);

    if (formNames.has(key)) {
      add({
        severity: 'warning',
        area: 'Custom fields',
        message: `Custom field "${key}" has the same name as a form field.`,
        detail:
          "It is only a default: the visitor's answer to that field overrides it (and the URL value, if enabled).",
      });
    }
  });

  // ── AI Generator blocks ──────────────────────────────────────────────────
  const TOKEN = /\{\{\s*([A-Za-z_][A-Za-z0-9_.-]*)\s*\}\}/g;
  (Array.isArray(fields) ? fields : [])
    .filter((field) => field?.key === 'AiGenerator')
    .forEach((field) => {
      const label = String(field.label ?? field.field_name ?? 'AI Generator').trim();
      const blocks = Array.isArray(field.blocks) ? field.blocks : [];

      // Prompt chips are {label, value} pairs (older forms saved plain
      // strings); only the value is required — an empty label falls back to
      // showing the value on the chip.
      const promptEntries = Array.isArray(field.prompts) ? field.prompts : [];
      const blankPrompts = promptEntries.filter((prompt: any) => {
        const value =
          typeof prompt === 'string' ? prompt : String(prompt?.value ?? '');
        return value.trim() === '';
      }).length;
      if (blankPrompts > 0) {
        add({
          severity: 'warning',
          area: 'Form fields',
          message: `AI Generator "${label}" has ${blankPrompts} prompt${blankPrompts > 1 ? 's' : ''} with no value.`,
          detail: 'Fill them in or remove them so the live form does not show blank chips.',
        });
      }

      if (blocks.length === 0) {
        add({
          severity: 'warning',
          area: 'Form fields',
          message: `AI Generator "${label}" has no content blocks configured.`,
          detail: 'Add at least one block so it has something to generate.',
        });
        return;
      }

      blocks.forEach((block: any, index: number) => {
        const blockLabel = String(block?.label ?? '').trim();
        const who = `AI Generator "${label}", block ${index + 1}${blockLabel ? ` ("${blockLabel}")` : ''}`;
        if (!blockLabel) {
          add({ severity: 'error', area: 'Form fields', message: `${who} has no label.` });
        }
        if (!String(block?.prompt ?? '').trim()) {
          add({ severity: 'error', area: 'Form fields', message: `${who} has no prompt.` });
        }
        for (const match of String(block?.prompt ?? '').matchAll(TOKEN)) {
          const name = match[1];
          if (!formNames.has(name)) {
            add({
              severity: 'warning',
              area: 'Form fields',
              message: `${who} references "{{${name}}}", which is not a field on this form.`,
              detail: 'It will resolve to an empty value.',
            });
          }
        }
      });
    });

  // A Workflow Preview block only has something to show when a workflow is set up.
  const hasPreviewBlock = (Array.isArray(fields) ? fields : []).some(
    (field) => field?.key === 'WorkflowPreview',
  );
  if (hasPreviewBlock && !(workflowSettings?.enabled && workflowSettings.templateId)) {
    add({
      severity: 'warning',
      area: 'Workflow',
      message: 'The Workflow Preview block has nothing to preview.',
      detail: 'Turn on workflow creation and choose a template in Advanced settings.',
    });
  }

  // ── Workflow ─────────────────────────────────────────────────────────────
  if (workflowOn && workflowSettings) {
    if (submitEveryStep) {
      add({
        severity: 'error',
        area: 'Workflow',
        message: 'Workflow creation will not run: this form submits every step.',
        detail: 'It would create one workflow per step, so it is skipped.',
      });
    }

    if (!workflowSettings.templateId) {
      add({
        severity: 'error',
        area: 'Workflow',
        message: 'Workflow creation is on but no template is selected.',
      });
    } else if (templatesFailed) {
      add({
        severity: 'warning',
        area: 'Workflow',
        message: "Couldn't check the workflow's required fields.",
        detail: 'The template list could not be loaded.',
      });
    } else if (templates) {
      const template = templates.find((t) => t.id === workflowSettings.templateId);
      if (!template) {
        add({
          severity: 'error',
          area: 'Workflow',
          message: `Workflow template "${workflowSettings.templateId}" no longer exists.`,
        });
      } else {
        // A custom field of the same name supplies a default (or URL value) too.
        const provided = (name: string) => formNames.has(name) || providedByCustom.has(name);
        template.fields
          .filter((f) => f.required && !provided(f.name))
          .forEach((f) =>
            add({
              severity: 'error',
              area: 'Workflow',
              message: `Required workflow field "${f.name}" (${f.label}) is missing from the form.`,
              detail: 'Add a form field, or a custom field with a default value, named exactly this.',
            }),
          );

        const optionalMissing = template.fields
          .filter((f) => !f.required && !provided(f.name))
          .map((f) => f.name);
        ['workflowName'].forEach((name) => {
          if (!provided(name)) optionalMissing.push(name);
        });
        if (optionalMissing.length) {
          add({
            severity: 'warning',
            area: 'Workflow',
            message: `${optionalMissing.length} optional workflow field${optionalMissing.length === 1 ? '' : 's'} not in the form.`,
            detail: `${list(optionalMissing)} — the defaults are used.`,
          });
        }

        // Variable bindings: each must still name a variable this template
        // declares and a source the form still has. Broken ones fall back to
        // the template default at runtime, so they warn, never error.
        const declaredVariables = new Set(
          (template.variables || []).map((variable) => variable.name),
        );
        const aiFields = new Set(
          (Array.isArray(fields) ? fields : [])
            .filter((field) => field?.key === 'AiGenerator')
            .map((field) => String(field?.field_name || '').trim())
            .filter(Boolean),
        );
        Object.entries(workflowSettings.variableSources || {}).forEach(
          ([name, source]) => {
            if (!declaredVariables.has(name)) {
              add({
                severity: 'warning',
                area: 'Workflow',
                message: `Variable "${name}" is mapped but this template does not declare it.`,
                detail: 'Remove the mapping in Advanced settings; it is ignored for now.',
              });
              return;
            }
            if (source?.type === 'ai' && !aiFields.has(source.field)) {
              add({
                severity: 'warning',
                area: 'Workflow',
                message: `Variable "${name}" is bound to AI Generator "${source.field}", which is not on this form.`,
                detail: 'The template default is used instead. Rebind it or restore the field.',
              });
            } else if (source?.type === 'field' && !provided(source.field)) {
              add({
                severity: 'warning',
                area: 'Workflow',
                message: `Variable "${name}" is bound to "${source.field}", which is not in the form.`,
                detail: 'The template default is used instead. Rebind it or add that field.',
              });
            } else if (source?.type === 'value' && !source.value.trim()) {
              add({
                severity: 'warning',
                area: 'Workflow',
                message: `Variable "${name}" is set to a fixed value, but the value is blank.`,
                detail: 'Fill it in or set the variable back to the template default.',
              });
            }
          },
        );
      }
    }
  }

  // Errors first; order within a severity follows the order found.
  return problems.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'error' ? -1 : 1));
};
